import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';

import { first, take } from 'rxjs/operators';

import { filterBoolean } from '@recruitee/common';

import {
  BasePortalHost,
  ComponentPortal,
  FocusTrap,
  FocusTrapFactory,
  PortalHostDirective,
  ScrollableDirective,
  TemplatePortal,
} from '../../core/core.module';
import { DialogConfig } from '../dialog-config';
import { DialogContentAlreadyAttachedError } from '../dialog-errors';

/** Possible states for the dialog container animation. */
export type DialogContainerAnimationState = 'void' | 'enter' | 'exit' | 'exit-start';

/**
 * Internal component that wraps user-provided dialog content.
 */
@Component({
  selector: 'rt-dialog-container',
  templateUrl: './dialog-container.component.html',
  styleUrls: ['./dialog-container.component.less'],
  host: {
    '[attr.role]': 'dialogConfig.role',
    '[class.has-padding]': 'hasPadding',
  },
})
export class DialogContainerComponent extends BasePortalHost implements OnDestroy {
  public dialogCloseVisible: boolean;

  @Input() public hasPadding: boolean = true;
  @Input() public dialogConfig: DialogConfig;

  @Output()
  public animationStateChange: EventEmitter<DialogContainerAnimationState> = new EventEmitter();

  @ViewChild(ScrollableDirective, { static: true }) public scrollable: ScrollableDirective;

  /** The portal host inside of this container into which the dialog content will be loaded. */
  @ViewChild(PortalHostDirective, { static: true }) private portalHost: PortalHostDirective;

  /** The class that traps and manages focus within the dialog. */
  private focusTrap: FocusTrap;

  /** Element that was focused before the dialog was opened. Save this to restore upon close. */
  private elementFocusedBeforeDialogWasOpened: HTMLElement | null = null;

  /** State of the dialog animation. */
  private animationState: DialogContainerAnimationState = 'enter';

  constructor(
    private ngZone: NgZone,
    private elementRef: ElementRef,
    private focusTrapFactory: FocusTrapFactory,
    private cdRef: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.dialogCloseVisible = !this.dialogConfig.disableClose && this.dialogConfig.showCloseButton;
    this.trapFocus();
  }

  public ngOnDestroy(): void {
    // When the dialog is destroyed, return focus to the element that originally had it before
    // the dialog was opened. Wait for the DOM to finish settling before changing the focus so
    // that it doesn't end up back on the <body>. Also note that we need the extra check, because
    // IE can set the `activeElement` to null in some cases.
    const toFocus = this.elementFocusedBeforeDialogWasOpened;

    this.ngZone.onMicrotaskEmpty
      .asObservable()
      .pipe(first())
      .subscribe(() => {
        if (toFocus && toFocus.focus) {
          toFocus.focus();
        }
        if (this.animationState !== 'exit') {
          this.animationState = 'exit';
          this.animationStateChange.emit('exit');
        }
        this.animationStateChange.complete();
      });

    if (this.focusTrap) {
      this.focusTrap.destroy();
    }
  }

  public attachComponentPortal<T>(portal: ComponentPortal<T>, index?: number): ComponentRef<T> {
    if (this.portalHost.hasAttached()) {
      throw new DialogContentAlreadyAttachedError();
    }

    return this.portalHost.attachComponentPortal(portal, index);
  }

  public attachTemplatePortal(portal: TemplatePortal): Map<string, any> {
    if (this.portalHost.hasAttached()) {
      throw new DialogContentAlreadyAttachedError();
    }

    return this.portalHost.attachTemplatePortal(portal);
  }

  // refreshing component from outside
  public detectChanges(): void {
    this.cdRef.detectChanges();
  }

  public close(): void {
    this.dialogConfig
      .closeGuard()
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.animationState = 'exit';
        // this._onAnimationStateChange.emit('exit-start');
        this.animationStateChange.emit('exit');
      });
  }

  /**
   * Moves the focus inside the focus trap.
   */
  private trapFocus(): void {
    if (!this.focusTrap) {
      this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
    }

    // If were to attempt to focus immediately, then the content of the dialog would not yet be
    // ready in instances where change detection has to run first. To deal with this, we simply
    // wait for the microtask queue to be empty.
    this.elementFocusedBeforeDialogWasOpened = <HTMLElement>document.activeElement;
    // No idea why we need this
    // this._focusTrap.focusFirstTabbableElementWhenReady();
  }
}
